/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import components */
import TimeSeriesPreview from '@/components/charts/TimeSeriesPreview';
import DemoSensorDeviceSelection from '@/components/tabs/demo/DemoSensorDeviceSelection';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import libraries */
import type { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import type { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import type { SensorMetadataEntry } from '@/lib/sensors/csv/csvToSensorMetadata';
import type {
    DemoSensorTarget,
    SensorGroupFilter,
} from '@/lib/sensors/demo/demoDeviceSensors';
import type {
    DemoSensorDeviceAvailability,
    DemoSensorDeviceGroupBy,
} from '@/lib/sensors/demo/demoSensorDeviceSelection';

/* Import configuration */
import type { TimeSeriesGroupKey } from '@/config/eeCheckConfig';

/* Import types */
import type { TabularOutputState, TimeSeriesToMode } from '@/types/ui';

interface TimeSeriesTabProps {
    deviceMetadata: SensorMetadataEntry | null;
    deviceSensor: DeviceSensor;
    deviceSensorCatalog: DeviceSensorCatalog;
    hasSession: boolean;
    isRequestingSeries: boolean;
    onRequestTimeSeries: () => void;
    onSelectedTimeSeriesAvailabilityChange: (availability: DemoSensorDeviceAvailability) => void;
    onSelectedTimeSeriesGroupChange: (group: TimeSeriesGroupKey) => void;
    onSelectedTimeSeriesGroupFilterChange: (groupFilter: SensorGroupFilter) => void;
    onSelectedTimeSeriesMeterChange: (meter: string) => void;
    onSelectedTimeSeriesSubsensorChange: (subsensor: string) => void;
    onSelectedTimeSeriesVirtualDeviceGroupByChange: (groupBy: DemoSensorDeviceGroupBy) => void;
    onTimeSeriesFromChange: (value: string) => void;
    onTimeSeriesTargetChange: (value: string) => void;
    onTimeSeriesToChange: (value: string) => void;
    onTimeSeriesToModeChange: (value: TimeSeriesToMode) => void;
    outputState: TabularOutputState;
    selectedTimeSeriesAvailability: DemoSensorDeviceAvailability;
    selectedTimeSeriesGroup: TimeSeriesGroupKey;
    selectedTimeSeriesGroupFilter: SensorGroupFilter;
    selectedTimeSeriesMeter: string;
    selectedTimeSeriesSubsensor: string;
    selectedTimeSeriesVirtualDeviceGroupBy: DemoSensorDeviceGroupBy;
    timeSeriesFrom: string;
    timeSeriesRequestFrom: string;
    timeSeriesRequestTo: string;
    timeSeriesSensorTargets: DemoSensorTarget[];
    timeSeriesTarget: string;
    timeSeriesTo: string;
    timeSeriesToMode: TimeSeriesToMode;
    timeSeriesVirtualDevices: string[];
}

function TimeSeriesTab({
    deviceMetadata,
    deviceSensor,
    deviceSensorCatalog,
    hasSession,
    isRequestingSeries,
    onRequestTimeSeries,
    onSelectedTimeSeriesAvailabilityChange,
    onSelectedTimeSeriesGroupChange,
    onSelectedTimeSeriesGroupFilterChange,
    onSelectedTimeSeriesMeterChange,
    onSelectedTimeSeriesSubsensorChange,
    onSelectedTimeSeriesVirtualDeviceGroupByChange,
    onTimeSeriesFromChange,
    onTimeSeriesTargetChange,
    onTimeSeriesToChange,
    onTimeSeriesToModeChange,
    outputState,
    selectedTimeSeriesAvailability,
    selectedTimeSeriesGroup,
    selectedTimeSeriesGroupFilter,
    selectedTimeSeriesMeter,
    selectedTimeSeriesSubsensor,
    selectedTimeSeriesVirtualDeviceGroupBy,
    timeSeriesFrom,
    timeSeriesRequestFrom,
    timeSeriesRequestTo,
    timeSeriesSensorTargets,
    timeSeriesTarget,
    timeSeriesTo,
    timeSeriesToMode,
    timeSeriesVirtualDevices,
}: TimeSeriesTabProps) {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-6">
            <DemoSensorDeviceSelection
                availability={selectedTimeSeriesAvailability}
                customer={selectedTimeSeriesGroup}
                deviceMetadata={deviceMetadata}
                deviceSensor={deviceSensor}
                deviceSensorCatalog={deviceSensorCatalog}
                groupBy={selectedTimeSeriesVirtualDeviceGroupBy}
                groupFilter={selectedTimeSeriesGroupFilter}
                onAvailabilityChange={onSelectedTimeSeriesAvailabilityChange}
                onCustomerChange={onSelectedTimeSeriesGroupChange}
                onDeviceChange={onSelectedTimeSeriesMeterChange}
                onGroupByChange={onSelectedTimeSeriesVirtualDeviceGroupByChange}
                onGroupFilterChange={onSelectedTimeSeriesGroupFilterChange}
                onSensorTargetChange={onSelectedTimeSeriesSubsensorChange}
                selectedDevice={selectedTimeSeriesMeter}
                selectedSensorTarget={selectedTimeSeriesSubsensor}
                sensorTargets={timeSeriesSensorTargets}
                showSensorTypeSelect={false}
                virtualDevices={timeSeriesVirtualDevices}
            />

            {selectedTimeSeriesMeter ? (
                <>

                    <section className="dashboard-panel flex items-stretch justify-start p-5">
                        <div className="grid w-full grid-cols-1 gap-y-4 text-left xl:grid-cols-4 xl:divide-x xl:divide-base-content">
                            <div className="flex flex-col gap-2 xl:pr-5">
                                <label className="text-sm font-semibold" htmlFor="timeseries-sensor-type-select">
                                    {t('Sensor type')}
                                </label>
                                <select
                                    id="timeseries-sensor-type-select"
                                    className="select select-bordered w-full"
                                    value={selectedTimeSeriesSubsensor}
                                    onChange={(event) => {
                                        onSelectedTimeSeriesSubsensorChange(event.target.value);
                                    }}
                                >
                                    {timeSeriesSensorTargets.map((target) => (
                                        <option key={`${target.group}-${target.sensorType}-${target.sensorName}`} value={target.sensorName ?? ''}>
                                            {`${target.sensorType} -> ${target.sensorName}`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex flex-col gap-2 xl:px-5">
                                <label className="text-sm font-semibold" htmlFor="timeseries-from">
                                    {t('From')}
                                </label>
                                <input
                                    id="timeseries-from"
                                    className="input input-bordered w-full"
                                    type="datetime-local"
                                    value={timeSeriesFrom}
                                    onChange={(event) => onTimeSeriesFromChange(event.target.value)}
                                />
                            </div>

                            <div className="flex flex-col gap-2 xl:px-5">
                                <label className="text-sm font-semibold" htmlFor="timeseries-to-mode">
                                    {t('To')}
                                </label>
                                <select
                                    id="timeseries-to-mode"
                                    className="select select-bordered w-full"
                                    value={timeSeriesToMode}
                                    onChange={(event) => {
                                        onTimeSeriesToModeChange(event.target.value as TimeSeriesToMode);
                                    }}
                                >
                                    <option value="now">NOW</option>
                                    <option value="custom">{t('Custom date')}</option>
                                </select>
                            </div>

                            <div className="flex flex-col gap-2 xl:pl-5">
                                <label className="text-sm font-semibold" htmlFor="timeseries-to">
                                    {t('To Date')}
                                </label>
                                <input
                                    id="timeseries-to"
                                    className="input input-bordered w-full"
                                    type="datetime-local"
                                    value={timeSeriesTo}
                                    onChange={(event) => onTimeSeriesToChange(event.target.value)}
                                    disabled={timeSeriesToMode === 'now'}
                                />
                            </div>
                        </div>
                    </section>

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <dl className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Customer')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">
                            {selectedTimeSeriesGroup === 'muenster' ? t('Münster') : t('Neutraubling')}
                        </dd>
                    </div>

                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('URN')}
                        </dt>
                        <dd className="mt-1">
                            <input
                                aria-label={t('URN')}
                                className="input input-bordered w-full font-mono text-sm"
                                type="text"
                                value={timeSeriesTarget}
                                onChange={(event) => onTimeSeriesTargetChange(event.target.value)}
                                spellCheck={false}
                            />
                        </dd>
                    </div>

                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Time series range')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">
                            {t('{from} to {to}', { from: timeSeriesRequestFrom, to: timeSeriesRequestTo })}
                        </dd>
                    </div>
                </dl>
            </section>

            <div>
                <button
                    className="btn btn-primary"
                    type="button"
                    onClick={onRequestTimeSeries}
                    disabled={!hasSession || isRequestingSeries}
                >
                    {isRequestingSeries ? t('Requesting time series...') : t('Request Time Series')}
                </button>
            </div>

            {outputState.outputMode === 'timeseries' && outputState.timeSeriesPreview ? (
                <TimeSeriesPreview preview={outputState.timeSeriesPreview} />
            ) : (
                <div>
                    <label className="mb-2 block text-sm font-semibold" htmlFor="response-output">
                        {t('Time series output')}
                    </label>
                    <textarea
                        id="response-output"
                        className="textarea textarea-bordered response-output w-full"
                        value={outputState.outputMode === 'raw' ? outputState.responseText : ''}
                        readOnly
                        spellCheck={false}
                        placeholder={t('The raw time series response will be shown here.')}
                    />
                </div>
            )}
                </>
            ) : (
                <div className="rounded-box border border-dashed border-base-300 px-4 py-8 text-center text-sm text-base-content/70">
                    {t('No sensor/device is available for the current selection.')}
                </div>
            )}
        </div>
    );
}

export default TimeSeriesTab;
